import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";

export interface IStatiegeldProduct {
  _id?: string;
  name?: string;
  gtin?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface IStore {
  /** State */
  statiegeldProduct: IStatiegeldProduct | null;
  hasNextPage: boolean;
  statiegeldProducts: IStatiegeldProduct[];
  addedStatiegeldProduct?: string | null;
  updatedStatiegeldProduct?: string | null;
  /** Callbacks */
  getStatiegeldProducts: (page?: number, limit?: number) => void;
  getStatiegeldProduct: (id: string) => void;
  addStatiegeldProduct: (data: IStatiegeldProduct) => void;
  addManyStatiegeldProduct: (data: IStatiegeldProduct[]) => void;
  deleteStatiegeldProduct: (sProductId: string) => void;
  updateStatiegeldProduct: (
    sProductId: string,
    data: { name: string; gtin: string }
  ) => void;
  clearAddedStatiegeldProduct: () => void;
  clearUpdatedStatiegeldProduct: () => void;
}

export const useStatiegeldProducts = create<IStore>()((set, get) => ({
  /** State */
  statiegeldProducts: [],
  hasNextPage: false,
  statiegeldProduct: null,
  addedArticle: null,
  /** Callbacks */
  getStatiegeldProducts: async (page = 1, limit = 20) => {
    useAppLoading.getState().setIsLoading(true);
    const currentStatiegeldProducts = get().statiegeldProducts;
    if (currentStatiegeldProducts?.length) {
      return;
    }
    try {
      const response = await axios.get(
        `tomra-products?page=${page}&limit=${limit}`
      );
      set((state) => ({
        ...state,
        hasNextPage: response?.data?.length === limit,
        statiegeldProducts:
          page === 1
            ? response?.data
            : [...state.statiegeldProducts, ...response?.data],
      }));
    } catch (error) {
      LOG.error("Error on fetching Statiegeld Products", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getStatiegeldProduct: async (id) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`tomra-products/${id}`);
      set({ statiegeldProduct: response.data });
    } catch (error) {
      LOG.error("Error on fetching Statiegeld Product", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addManyStatiegeldProduct: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("tomra-products/many", body);
      if (response?.data) {
        const newStatiegeldProducts = response.data;
        set((state) => ({
          statiegeldProducts: [
            ...newStatiegeldProducts,
            ...state.statiegeldProducts,
          ],
          addedStatiegeldProduct: newStatiegeldProducts?.[0]._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding new Statiegeld Product", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addStatiegeldProduct: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("tomra-products", body);
      if (response?.data) {
        const newStatiegeldProduct = response.data;
        set((state) => ({
          statiegeldProducts: [
            newStatiegeldProduct,
            ...state.statiegeldProducts,
          ],
          addedStatiegeldProduct: newStatiegeldProduct._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding new Statiegeld Product", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteStatiegeldProduct: async (sProductId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`tomra-products/${sProductId}`);
      set((state) => ({
        statiegeldProducts: state.statiegeldProducts.filter(
          (sProduct) => sProduct._id !== sProductId
        ),
      }));
    } catch (error) {
      LOG.error("Error on deleting a Statiegeld Product", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateStatiegeldProduct: async (sProductId, body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`tomra-products/${sProductId}`, body);
      if (response?.data) {
        const updatedSProduct = response.data;
        set((state) => ({
          statiegeldProducts: state.statiegeldProducts.map((sProduct) => {
            if (sProduct._id === updatedSProduct._id) {
              return updatedSProduct;
            }
            return sProduct;
          }),
          updatedStatiegeldProduct: updatedSProduct._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating a Statiegeld Product", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedStatiegeldProduct: () => {
    set({ addedStatiegeldProduct: null });
  },
  clearUpdatedStatiegeldProduct: () => {
    set({ updatedStatiegeldProduct: null });
  },
}));
